// Authentication API service
import { mockAuthAPI } from './mockAuthAPI';
import { OTP_TYPES, type OTPType } from './otpAuthService';
import { buildApiUrl } from '@/utils/apiHelpers';
import { apiErrorMessage } from '@/utils/apiErrorMessage';


export interface LoginRequest {
  email?: string;
  phone?: string;
  password: string;
}

export interface LoginResponse {
  message: string;
  user: {
    id: string;
    email: string;
    fullName: string;
    phone: string | null;
    role: string;  // Note: login returns role as string, profile returns with permissions
    permissions?: string[];  // Added by profile endpoint
    status: string;
    emailVerified: boolean;
    phoneVerified: boolean;
    lastLoginAt: string;
    createdAt: string;
    updatedAt: string;
  };
  accessToken: string;
  refreshToken: string;
}

// 🔧 FIXED OTP Interfaces
export interface SendOTPRequest {
  phone: string;
  type: OTPType; // Must be exact: "registration" | "login" | "password_reset"
}

export interface SendOTPResponse {
  success: boolean;
  message: string;
  data: {
    phone: string;
    type: string;
    expiresIn: number;
  };
}

export interface VerifyOTPRequest {
  phone: string;
  otp: string;
  type: OTPType; // MUST match the type used in send-otp
}

export interface VerifyOTPResponse {
  success: boolean;
  message: string;
  data: {
    user?: any;
    token?: string;
    refreshToken?: string;
  };
}

class AuthAPIService {
  private currentOTPContext: { phone: string; type: OTPType } | null = null;

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      // Use ADMIN login endpoint (checks admin_users table, not users table)
      // Mobile app uses /api/v1/auth/login (users table)
      // Admin dashboard uses /api/v1/auth/admin/login (admin_users table)
      const url = buildApiUrl('/auth/admin/login', 'v1');

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Allow cookies to be set
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Login failed' }));
        const errorMessage = apiErrorMessage(errorData, 'Login failed');
        console.error('❌ Admin Login Failed:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(errorMessage);
      }

      const result = await response.json();


      return result;
    } catch (error) {
      // Don't fallback to mock for admin login - it could mask real issues
      console.error('Admin login error:', error instanceof Error ? error.message : String(error));
      throw error;
    }
  }

  // 🔧 FIXED: Send OTP with backend-compatible format
  async sendOTP(request: SendOTPRequest): Promise<SendOTPResponse> {
    try {
      // 🚨 CRITICAL FIX: Send exactly what backend expects (no +1 prefix)
      const backendRequest = {
        phone: request.phone.replace(/^\+1/, '').replace(/\D/g, ''), // Remove +1 and non-digits
        type: request.type  // Use "registration", "login", or "password_reset"
      };

      // 🔍 ENHANCED DEBUGGING: Log exact request details

      // Store normalized context to ensure matching in verify
      this.currentOTPContext = { phone: backendRequest.phone, type: backendRequest.type };

      const response = await fetch(buildApiUrl('/auth/send-otp', 'v1'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendRequest),
      });


      const result = await response.json();

      if (!response.ok) {
        console.error('❌ Send OTP failed:');
        console.error('  Status:', response.status);
        console.error('  Result:', result);
        throw new Error(apiErrorMessage(result, 'Failed to send OTP'));
      }

      return result;
    } catch (error) {
      console.error('❌ Send OTP error:', error);
      throw error;
    }
  }

  // 🔧 FIXED: Verify OTP with backend-compatible format
  async verifyOTP(request: VerifyOTPRequest): Promise<VerifyOTPResponse> {
    try {
      // 🚨 CRITICAL FIX: Send exactly what backend expects (no +1 prefix)
      const backendRequest = {
        phone: request.phone.replace(/^\+1/, '').replace(/\D/g, ''), // Remove +1 and non-digits
        otp: request.otp.toString(),
        type: request.type  // Use "registration", "login", or "password_reset"
      };

      // 🔍 ENHANCED DEBUGGING: Log exact request details

      // Validate against stored context
      if (this.currentOTPContext) {
        
        if (request.type !== this.currentOTPContext.type) {
        }
      }

      const response = await fetch(buildApiUrl('/auth/verify-otp', 'v1'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendRequest),
      });


      const result = await response.json();

      if (!response.ok) {
        console.error('❌ Verify OTP failed:');
        console.error('  Status:', response.status);
        console.error('  Result:', result);
        throw new Error(apiErrorMessage(result, 'Failed to verify OTP'));
      }

      
      // Clear context after successful verification
      this.currentOTPContext = null;
      
      return result;
    } catch (error) {
      console.error('❌ Verify OTP error:', error);
      throw error;
    }
  }

  // 🎯 High-level OTP flows with guaranteed type consistency
  async loginWithOTP(phone: string, otp: string): Promise<VerifyOTPResponse> {
    const type = OTP_TYPES.LOGIN;
    
    // If we haven't sent OTP yet, send it first
    if (!this.currentOTPContext || this.currentOTPContext.type !== type) {
      await this.sendOTP({ phone, type });
    }
    
    return this.verifyOTP({ phone, otp, type });
  }

  async registerWithOTP(phone: string, otp: string): Promise<VerifyOTPResponse> {
    const type = OTP_TYPES.REGISTRATION;
    
    // If we haven't sent OTP yet, send it first
    if (!this.currentOTPContext || this.currentOTPContext.type !== type) {
      await this.sendOTP({ phone, type });
    }
    
    return this.verifyOTP({ phone, otp, type });
  }

  async resetPasswordWithOTP(phone: string, otp: string): Promise<VerifyOTPResponse> {
    const type = OTP_TYPES.PASSWORD_RESET;
    
    // If we haven't sent OTP yet, send it first
    if (!this.currentOTPContext || this.currentOTPContext.type !== type) {
      await this.sendOTP({ phone, type });
    }
    
    return this.verifyOTP({ phone, otp, type });
  }

  // Forgot Password API methods
  async forgotPassword(identifier: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(buildApiUrl('/auth/admin/forgot-password', 'v1'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reset code');
      }

      return {
        success: true,
        message: data.message || 'Reset code sent successfully'
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to send reset code'
      };
    }
  }

  async resetPassword(identifier: string, otp: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(buildApiUrl('/auth/admin/reset-password', 'v1'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier,
          otp,
          newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reset password');
      }

      return {
        success: true,
        message: data.message || 'Password reset successfully'
      };
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to reset password'
      };
    }
  }

  // Admin Profile Management API methods
  async getCurrentProfile(): Promise<{ success: boolean; user?: any; message?: string }> {
    try {
      const token = localStorage.getItem('token');


      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(buildApiUrl('/admin/profile', 'v1'), {
        method: 'GET',
        credentials: 'include', // Send cookies with request
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();


      if (!response.ok) {
        const errorMessage = data.message || `HTTP ${response.status}: ${response.statusText}`;
        console.error('❌ API Error:', errorMessage);
        throw new Error(errorMessage);
      }

      return {
        success: true,
        user: data.user || data,
        message: 'Profile loaded successfully'
      };
    } catch (error) {
      console.error('Get profile error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to load profile'
      };
    }
  }

  async updateProfile(profileData: {
    email?: string;
    phone?: string;
    fullName?: string;
  }): Promise<{ success: boolean; message: string; user?: any }> {
    try {
      const response = await fetch(buildApiUrl('/admin/profile', 'v1'), {
        method: 'PUT',
        credentials: 'include', // Send cookies with request
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update profile');
      }

      return {
        success: true,
        message: data.message || 'Profile updated successfully',
        user: data.user
      };
    } catch (error) {
      console.error('Update profile error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update profile'
      };
    }
  }

  async logout(): Promise<void> {
    try {
      // Clear OTP context
      this.currentOTPContext = null;
      
      // Try real API logout
      await fetch(buildApiUrl('/auth/logout', 'v1'), {
        method: 'POST',
        credentials: 'include', // Send cookies with request
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Always clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }

  async refreshToken(refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> {
    try {
      const response = await fetch(buildApiUrl('/auth/refresh', 'v1'), {
        method: 'POST',
        credentials: 'include', // Send cookies with request
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      // Fallback to mock
      return mockAuthAPI.refreshToken(refreshToken);
    }
  }

  async getCurrentUser(): Promise<any> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(buildApiUrl('/auth/me', 'v1'), {
        method: 'GET',
        credentials: 'include', // Send cookies with request
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get current user');
      }

      const result = await response.json();
      return result.user;
    } catch (error) {
      // Fallback to mock
      return mockAuthAPI.getCurrentUser();
    }
  }

  async changePassword(currentPassword: string, newPassword: string, confirmNewPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(buildApiUrl('/admin/change-password', 'v1'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword, confirmNewPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(apiErrorMessage(data, 'Failed to change password'));
      }

      return {
        success: true,
        message: data.message || 'Password changed successfully'
      };
    } catch (error) {
      console.error('Change password error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to change password'
      };
    }
  }

  // Debug method to check current OTP context
  getCurrentOTPContext(): { phone: string; type: OTPType } | null {
    return this.currentOTPContext;
  }

  // Clear OTP context manually if needed
  clearOTPContext(): void {
    this.currentOTPContext = null;
  }
}

export const authAPI = new AuthAPIService();